import type { GrammarTopic } from './types';

export const connectingTopics: GrammarTopic[] = [
  {
    slug: 'adjectives-ed-ing',
    title: 'Adjectives Ended in -ed or in -ing',
    blocks: [
      {
        kind: 'p',
        text: 'Many adjectives of the English language end either in -ED or in -ING. Yes, that\'s correct, they are not only endings that we use for verbs.',
      },
      { kind: 'h3', text: '1. An adjective that ends in -ING is used to describe' },
      {
        kind: 'p',
        text: 'A permanent characteristic of a person, a thing or a situation. -ANTE',
      },
      { kind: 'h3', text: '2. An adjective that ends in -ED is used to describe' },
      {
        kind: 'p',
        text: 'A feeling (or how a person feels) or an emotion. It is used to describe a temporary characteristic. Since only people (and some animals) have feelings, -ed adjectives cannot be used to describe an object or situation. -ADO',
      },
      { kind: 'h3', text: 'Compare the difference' },
      {
        kind: 'examples',
        items: [
          'My boyfriend is **bored**. → My boyfriend feels bored at the moment.',
          'My boyfriend is **boring**. → My boyfriend is a boring person.',
        ],
      },
      {
        kind: 'p',
        text: 'You can use these adjectives to describe people or situations but be careful that you are using the correct adjective. For example, there is a big difference in meaning between:',
      },
      {
        kind: 'examples',
        items: [
          'I was **shocked** by how **shocking** the accident was last night.',
          'They were **frightened** by the **frightening** roller-coaster ride!',
          'I am **annoyed** by how **annoying** that person in front of us is.',
          'I am **confused**. → I don\'t understand something.',
          'I am **confusing**. → I will cause you to be confused.',
        ],
      },
    ],
  },
  {
    slug: 'attributive-adjectives',
    title: 'Attributive Adjectives: Describing People',
    blocks: [
      {
        kind: 'p',
        text: 'Attributive adjectives are words that will expand on specific traits, qualities, and features and they can be used to talk about yourself or about someone else. It is important that you know some attributive adjectives to maintain conversations that focus on personality traits, such as in job interviews.',
      },
      {
        kind: 'p',
        text: 'Below, you will find a list of intermediate attributive adjectives:',
      },
      {
        kind: 'list',
        items: [
          'Patient – someone who is not in a hurry and can spend time helping other people.',
          'Hard-working – a person who puts a lot of effort into their work.',
          'Calm – a person who is relaxed and not nervous.',
          'Confident – someone who is self-assured and able to succeed.',
          'Intelligent – someone who has the ability to learn and understand things quickly.',
          'Reliable/Trustworthy – a person who can be depended on to do what they say.',
          'Sensible – a person who can be trusted to make the right decision.',
          'Sociable – someone who is friendly and enjoys the company of other people.',
          'Competitive – a person who likes to win at games or sports. They may be bad losers.',
          'Charming – someone who has an attractive personality that makes people like them.',
          'Ambitious – someone who has a strong desire to do well or to be successful.',
          'Generous – someone who is happy to give more money, time, and help than is usual.',
          'Arrogant – someone who thinks that they are better than other people.',
          'Aggressive – someone who often fights and argues with other people.',
          'Cheerful – someone who feels happy.',
          'Easy-going – someone who is relaxed and not worried about what other people do, someone who is very easy to get on with.',
          'Responsible – someone who is able to act sensibly and make his own decisions.',
          'Honest – someone who always tells the truth.',
          'Tolerant – someone who is able to accept other people’s ideas even if they don’t agree with them.',
          'Flexible – someone who adapts quickly to new situations and doesn’t mind change.',
          'Conscientious – someone who takes care in doing things properly and carefully.',
          'Assertive – someone who behaves confidently in order that people take notice of what they say.',
          'Welcoming – someone who is going to make you feel like you belong there the moment you walk into the room. They make you feel accepted and happy.',
          'Spontaneous – someone who acts in an open and natural way, capable of making decisions without planning.',
          'Thoughtful – someone who is always thinking about others around them and about what they might need or what they might want.',
          'Considerate – someone who shows concern for others and thinks about how a situation or something might affect someone else.',
        ],
      },
      { kind: 'h3', text: 'Advanced Attributive Adjectives' },
      {
        kind: 'list',
        items: [
          'Dull – a boring person, someone who has no personality.',
          'Thick – stupid, rude.',
          'Down-to-earth – someone who is sensible and realistic in things they say or do.',
          'Pretentious – someone who pretends to be very important.',
          'Witty/Quick-witted – someone who is very quick thinking and clever with words.',
          'Ruthless / Cruel – someone who is determined to get what they want and not caring about others.',
          'Shrewd – someone who is good at judging people and situations.',
          'Pushy – someone who tries hard to get what they want, especially in a rude way.',
          'Conceited – someone who thinks that they are very important, clever, etc.',
          'Diffident – someone who is not confident, a shy person.',
          'Chirpy – someone who is really happy, lively, and excited about the day ahead.',
          'Resourceful – someone who is able to use whatever they have at the time to solve problems or to reach their goals.',
        ],
      },
    ],
  },
  {
    slug: 'adverbs-of-degree',
    title: 'Adverbs of Degree',
    blocks: [
      {
        kind: 'p',
        text: 'We use adverbs of degree to qualify what we are talking about. We might tell our friends that we are happy, but we may want to explain the degree of our happiness:',
      },
      {
        kind: 'examples',
        items: [
          'John was **totally** exhausted.',
          'He is **extremely** intelligent.',
          'I am **slightly** pushy in my decisions.',
        ],
      },
      {
        kind: 'p',
        text: 'Consider the following chart to help you define the intensity of something.',
      },
      {
        kind: 'p',
        text: 'How strongly adverbs of degree modify adverbs and adjectives:',
      },
      {
        kind: 'table',
        header: ['Mild (+)', 'Medium (++)', 'Strong (+++)', 'Absolute (++++)'],
        rows: [
          [
            'a little, a bit, slightly',
            'fairly, pretty, quite, rather, somewhat',
            'very, really, remarkably, awfully, extremely, highly, terribly, incredibly, particularly, deeply, enormously, greatly, mostly, strongly',
            'totally, utterly, completely, absolutely, entirely, perfectly, thoroughly',
          ],
        ],
      },
    ],
  },
  {
    slug: 'uses-of-rather',
    title: 'The Uses of Rather',
    blocks: [
      {
        kind: 'p',
        text: 'The word “rather” can have several meanings in English. Although all of them are rather simple (see what I did there?), using this expression will elevate your English level. Let’s take a look at these uses:',
      },
      { kind: 'h3', text: '1. “Rather” as an adverb to indicate a degree' },
      {
        kind: 'p',
        text: 'We can use “rather” to give emphasis to an adjective or adverb. It has a similar meaning to “quite” but it is more formal.',
      },
      {
        kind: 'examples',
        items: [
          'I’m **rather** busy this week.',
          'I had some **rather** bad news today.',
          'You’ll be seeing **rather** a lot of me over the next few weeks.',
          'He **rather** liked the idea of a well-paid job in Japan.',
        ],
      },
      { kind: 'h3', text: '2. “Rather than” to indicate alternatives and preferences' },
      {
        kind: 'p',
        text: 'We use “rather than” to give more importance to one thing when two alternatives or preferences are being compared, it is similar to “instead of”.',
      },
      {
        kind: 'examples',
        items: [
          'He wanted to be an actor **rather than** a comedian.',
          'Can we come over on Saturday **rather than** Friday?',
        ],
      },
      { kind: 'h3', text: '3. “Would rather” to indicate preferences' },
      {
        kind: 'p',
        text: 'We use “would rather” or “’d rather” to talk about preferring one thing to another.',
      },
      {
        kind: 'examples',
        items: [
          '**I’d rather** stay at home than go out tonight.',
          '**Would** you **rather** give or receive gifts?',
        ],
      },
    ],
  },
  {
    slug: 'phrasal-verbs-general',
    title: 'Phrasal Verbs: General',
    blocks: [
      {
        kind: 'p',
        text: 'Phrasal verbs are verbal forms formed by a verb and another particle, such as a preposition or an adverb. The junction of these words forms one syntactic meaning. Although some phrasal verbs are not so evident in their meaning (e.g. put up with = tolerate), others have been used by English learners since the first English class (e.g. wake up, pick up, turn on).',
      },
      {
        kind: 'p',
        text: 'Let’s take a look at some of the most important phrasal verbs for your level.',
      },
      {
        kind: 'table',
        header: ['Phrasal verb', 'Definition', 'Example'],
        rows: [
          ['To bring up', 'To introduce a topic', 'I knew he was going to **bring up** the 2014 results at the meeting.'],
          ['To carry on', 'To continue', 'I will **carry on** with the job after lunch.'],
          ['To come up with', 'To have an idea', 'We need to **come up with** some solutions, fast!'],
          ['To cope with', 'To manage / to withstand', 'In the end he changed jobs because he couldn’t **cope with** the pressure.'],
          ['To deal with', 'To manage / to withstand / to have a relationship with (someone)', 'Do you **deal with** Susan directly?'],
          ['To find out', 'To gain knowledge about something', 'I finally **found out** how to forward my mail from one email account to another.'],
          ['To get on/along (with)', 'To have a good relationship, to get along', 'After a few weeks I found we actually **got on** quite well.'],
          ['To get over (something)', 'To recover, surpass, overcome, move on', 'I was upset that I didn’t get the promotion, but I **got over** it after a while.'],
          ['To get rid of', 'To not possess any more, discard, throw away', 'We really need to **get rid of** this old management system.'],
          ['To look forward to', 'To be excited about something in the future', 'I’m really **looking forward to** having an extended weekend next week.'],
          ['To put (something) off', 'To postpone', 'I haven’t done my taxes yet. I’ve been **putting** it **off** for a long time.'],
          ['To put up with', 'To tolerate', 'We have had to **put up with** a lot of problems on this project.'],
          ['To rule out', 'To discard an idea', 'I think we can **rule out** the possibility of getting more news today.'],
          ['To run out of', 'To have a decreasing or zero amount of something', 'The printer is **running out of** toner.'],
          ['To sort (something) out', 'To organize or resolve a problem', 'There was some confusion with the new work schedule. No one is sure who works on Sunday. We’re calling a meeting to **sort** it **out**.'],
          ['To look after', 'To take care of', 'He is **looking after** his dog.'],
          ['To get by', 'To survive, “se virar”', 'I can **get by** in English, but my French is a little rusty.'],
          ['Brush up on', 'Improve something you are rusty at', 'I ought to **brush up on** my German grammar as I commit far too many errors.'],
          ['Take up', 'Start doing an activity', 'Are you interested in **taking up** a new hobby in the coming year?'],
          ['Get around', 'Move from A to B', 'I usually **get around** my city on foot as everything is just around the corner. It is a small and quaint city.'],
          ['Drop out (of)', 'Leave/Stop Attending', 'I would think twice before **dropping out of** university. It could be a massive mistake as you only have one year to go.'],
          ['Come across', 'Find by coincidence', 'Wouldn’t it be great if you **came across** a $50 bill on the floor as you were walking down the street?'],
        ],
      },
      {
        kind: 'p',
        text: 'There is no way of memorizing all existent phrasal verbs but you can surely study them when you read a text or hear them in a video. Maximizing your contact with English and being aware of grammatical structures is key to learning grammar.',
      },
    ],
  },
  {
    slug: 'phrasal-verbs-work',
    title: 'Phrasal Verbs: Work-Related',
    blocks: [
      {
        kind: 'table',
        header: ['Phrasal Verb', 'Meaning', 'Example'],
        rows: [
          ['Follow up', 'To continue communication or action after an initial meeting or conversation.', '"I’ll **follow up** with you next week to discuss our potential collaboration."'],
          ['Reach out', 'To initiate contact with someone, usually for help or to make a connection.', '"Feel free to **reach out** if you have any questions or need further information."'],
          ['Set up', 'To arrange or organize something, like a meeting or an event.', '"Let’s **set up** a meeting to discuss this further."'],
          ['Put off', 'To postpone or delay something.', '"We had to **put off** our meeting until next week due to scheduling conflicts."'],
          ['Get along', 'To have a good relationship with someone.', '"I was pleased to see how well we **got along** during the conference."'],
          ['Come across', 'To seem or appear in a certain way to others.', '"He **comes across** as very confident and knowledgeable."'],
          ['Run into', 'To meet someone unexpectedly.', '"I **ran into** an old colleague at the networking event."'],
          ['Bring up', 'To start talking about a particular topic.', '"She **brought up** an interesting point about market trends during our conversation."'],
          ['Break into', 'To enter a new industry, market, or area of expertise.', '"It can be challenging to **break into** the tech industry, but networking can help."'],
          ['Catch up', 'To talk to someone you haven’t seen in a while to find out what they’ve been doing.', '"Let’s **catch up** over coffee and discuss how our projects are going."'],
          ['Turn down', 'To refuse or decline an offer or invitation.', '"I had to **turn down** the invitation because of a prior commitment."'],
          ['Come up with', 'To think of or produce a new idea, plan, or solution.', '"We need to **come up with** a strategy to attract more clients."'],
          ['Hand out', 'To distribute something to a group of people.', '"I’ll **hand out** business cards during the networking session."'],
          ['Work out', 'To find a solution or come to an agreement.', '"We managed to **work out** the details of our partnership."'],
          ['Touch base', 'To make brief contact with someone to check in or update them.', '"Let’s **touch base** next week to see how the project is progressing."'],
          ['Go over', 'To review or examine something carefully.', '"We can **go over** the proposal together before the meeting."'],
          ['Take on', 'To accept a task or responsibility.', '"She’s **taking on** more responsibilities at work this year."'],
          ['Step down', 'To resign from a position or responsibility.', '"The CEO decided to **step down** after 10 years in the role."'],
          ['Bring in', 'To attract or generate new clients, business, or ideas.', '"Our marketing strategy has been successful in **bringing in** new customers."'],
          ['Back up', 'To support someone or something.', '"I appreciate you **backing** me **up** during the presentation."'],
        ],
      },
    ],
  },
  {
    slug: 'giving-feedback',
    title: 'Giving Feedback',
    blocks: [
      {
        kind: 'p',
        text: 'Feedback is often confused with giving advice, praise, and evaluation, but none of these descriptions are exactly accurate. What constitutes effective feedback?',
      },
      {
        kind: 'p',
        text: 'What constitutes helpful feedback to me is information that is goal-referenced, actionable, specific, and has an immediate impact. It should be constructive, descriptive, and non-judgmental. The mnemonic I use to provide better feedback and achieve learning goals is SMART: specific, measurable, achievable, realistic, and timely, as described here:',
      },
      { kind: 'h3', text: 'Specific' },
      {
        kind: 'p',
        text: 'Avoid using ambiguous language, for example, “Your presentation was great.” Be specific about what made the presentation “great,” such as, “Starting your presentation off with a provocative question got my attention.”',
      },
      { kind: 'h3', text: 'Measurable' },
      {
        kind: 'p',
        text: 'Suggest quantifiable objectives to meet so there is no uncertainty about what the goals are. For example, “Next time, try a summary slide with one or two take-home points for the audience.”',
      },
      { kind: 'h3', text: 'Achievable' },
      {
        kind: 'p',
        text: 'The goal of the presentation should be attainable. For example, “Trim your slides to no more than six lines per slide and no more than six words per line; otherwise, you are just reading your slides.”',
      },
      { kind: 'h3', text: 'Realistic' },
      {
        kind: 'p',
        text: 'The feedback you give should relate to the goal the presenter is trying to achieve.',
      },
      { kind: 'h3', text: 'Timely' },
      {
        kind: 'p',
        text: 'Feedback given directly after the completion of the presentation is more effective than feedback provided at a later date.',
      },
      { kind: 'h3', text: 'Relevant or irrelevant feedback?' },
      {
        kind: 'p',
        text: 'Classify the following comments into E (effective) or I (ineffective)!',
      },
      {
        kind: 'list',
        items: [
          '( ) “Eye contact”',
          '( ) “You looked at your notes more than you looked at the audience during the first thirty seconds of your speech.”',
          '( ) “Good job”',
          '( ) “You were able to explain our company’s new marketing strategy in a way that even I, as an engineer, could make sense of. The part about our new crisis communication plan wasn’t as clear. Perhaps you could break it down the same way you did the marketing strategy to make it clearer for people like me who are outside the public relations department.”',
          '( ) “Don’t be nervous”',
          '( ) “I know the first speech is tough, but remember that we’re all in the same situation, and we’re all here to learn. I tried the breathing exercises discussed in the book and they helped calm my nerves. Maybe they’ll work for you, too?”',
        ],
      },
      {
        kind: 'p',
        text: 'Sources: https://ascopost.com/issues/december-25-2019/how-to-give-effective-presentation-feedback/ https://open.lib.umn.edu/communication/chapter/5-4-listenable-messages-and-effective-feedback/',
      },
      { kind: 'h3', text: 'Extra source for your practice…' },
      {
        kind: 'list',
        items: [
          'Watch this 5-minute TED video on the pillars of great feedback: https://youtu.be/wtl5UrrgU8c',
          'Read over these 30 presentation feedback examples to inspire yourself when you need to give your own: https://www.betterup.com/blog/presentation-feedback-examples',
        ],
      },
    ],
  },
  {
    slug: 'expressing-opinions',
    title: 'Expressing Opinions',
    blocks: [
      { kind: 'h3', text: 'Ways of expressing agreement' },
      {
        kind: 'list',
        items: [
          'I really **think/believe so**, too.',
          '**I couldn’t agree more**.',
          'I have **come to the same conclusion**.',
          'I **hold the same opinion**.',
          'I have **no objection** whatsoever.',
          'I see what you mean, and I (must) **agree with you**.',
          'I **see it that way**, too.',
          'I **share your opinion/view**.',
          'You’re (completely / totally / absolutely) **right**.',
          'You **have a point** there.',
          'You’ve **made a good point**.',
          'We are **of one mind** (on…)',
          'We are **of the same mind** (on…)',
          'That’s (so / completely / absolutely / undeniably) **true**.',
          'I **agree with you up to a point**.',
          '**I see what you mean, but**…',
          '**That idea is OK, but**…',
        ],
      },
      { kind: 'h3', text: 'Ways of expressing disagreement' },
      {
        kind: 'list',
        items: [
          '**I’m sorry but**…',
          '**That might be true, but**…',
          '**I beg to differ**.',
          'I **don’t agree with you** on that / what you say.',
          'I **don’t share your view**.',
          'I **think otherwise**.',
          'I **take a different view**.',
          'I believe your argument **doesn’t hold water**.',
          'That **doesn’t make much sense** to me.',
          'That’s **not always / necessarily true**.',
        ],
      },
      { kind: 'h3', text: 'Other ways of expressing your opinion' },
      {
        kind: 'list',
        items: [
          '**I think / consider / find / feel / believe / suppose / presume / assume that**…',
          '**I would say that**…',
          '**I hold the opinion/view that**…',
          '**I take the view that**…',
          '**I guess that**…',
          '**I bet that**…',
          '**I gather that**…',
          '**It seems to me that**…',
          '**It is my impression that**…',
          '**In my eyes**,…',
          '**From my viewpoint**,…',
          '**To be honest**,…',
          '**My opinion / view / belief / impression is that**…',
          '**My personal view is that**…',
          '**As far as I’m concerned**,…',
          '**As for me / As to me**…',
          '**As I see it**…',
          '**The way I see it**…',
          '**Personally speaking**,…',
        ],
      },
    ],
  },
  {
    slug: 'english-global-language',
    title: 'English: A Global Language',
    blocks: [
      {
        kind: 'p',
        text: 'English is a global language. Today, it is used by millions of people in many different countries. In fact, more people speak English as an additional language than as a first language.',
      },
      {
        kind: 'p',
        text: 'English is an official language in more than 60 countries, and it is used internationally in business, education, science, and on the internet. Because of this, English has developed many different varieties, often called World Englishes. These varieties reflect the history, culture, and other languages spoken in each region.',
      },
      {
        kind: 'p',
        text: 'Source: https://commons.wikimedia.org/wiki/File:World_map_percentage_english_speakers_by_country.png',
      },
      { kind: 'h3', text: 'American and British English' },
      {
        kind: 'p',
        text: 'These are two of the most studied varieties of English. They differ in vocabulary, spelling, and pronunciation.',
      },
      { kind: 'h3', text: 'Vocabulary' },
      {
        kind: 'p',
        text: 'Different English varieties sometimes use different words for the same thing.',
      },
      {
        kind: 'table',
        header: ['American', 'British'],
        rows: [
          ['Last name', 'Surname'],
          ['Parentheses', 'Brackets'],
          ['Leash', 'Lead'],
          ['Parking lot', 'Car park'],
          ['Movie', 'Film'],
          ['Pharmacy/Drugstore', 'Chemist'],
          ['Movie theater', 'Cinema'],
          ['Picture', 'Photo'],
          ['Pain reliever', 'Pain killer'],
          ['Purse', 'Handbag'],
          ['Bro/pal', 'Mate'],
          ['Refrigerator', 'Fridge'],
          ['Pants', 'Trousers'],
          ['Restroom/Washroom', 'Toilet'],
          ['Roommate', 'Flatmate'],
          ['Broil', 'Grill'],
          ['Appetizer', 'Starter'],
          ['Gas', 'Petrol'],
          ['Cookie', 'Biscuit'],
          ['Elevator', 'Lift'],
          ['French fries', 'Chips'],
          ['Sweater', 'Jumper'],
          ['Zucchini', 'Courgette'],
          ['Vacation', 'Holiday'],
          ['Robe (bathrobe)', 'Dressing gown'],
          ['Cellphone', 'Mobile phone'],
          ['Soccer', 'Football'],
          ['Sneakers/Runners', 'Trainers'],
          ['Eggplant', 'Aubergine'],
          ['Beet', 'Beetroot'],
          ['Pitcher', 'Jug'],
          ['Garbage', 'Rubbish'],
        ],
      },
      {
        kind: 'p',
        text: 'In many cases, Canadian English is closer to American vocabulary, but there are also unique Canadian expressions such as: washroom (bathroom) and loonie (one dollar coin).',
      },
      { kind: 'h3', text: 'Spelling' },
      {
        kind: 'p',
        text: 'Some words are spelled differently in British and American English.',
      },
      {
        kind: 'table',
        header: ['American', 'British', 'Canadian'],
        rows: [
          ['color', 'colour', 'colour'],
          ['favorite', 'favourite', 'favourite'],
          ['center', 'centre', 'centre'],
          ['theater', 'theatre', 'theatre'],
          ['analyze', 'analyse', 'analyze'],
          ['cancelation', 'cancellation', 'cancellation'],
          ['catalog', 'catalogue', 'catalog'],
          ['pediatrician', 'paediatrician', 'pediatrician'],
        ],
      },
      { kind: 'h3', text: 'Pronunciation' },
      {
        kind: 'p',
        text: 'There are also pronunciation differences between English varieties.',
      },
      { kind: 'h3', text: 'I. Pronouncing the letter R' },
      {
        kind: 'p',
        text: 'In most American and Canadian accents, the R is pronounced clearly.',
      },
      { kind: 'examples', items: ['park → /park/'] },
      {
        kind: 'p',
        text: 'In many British accents, the R is not pronounced unless it is followed by a vowel.',
      },
      { kind: 'examples', items: ['park → /pɑːk/'] },
      { kind: 'h3', text: 'II. The “T” sound' },
      {
        kind: 'p',
        text: 'In American and Canadian English, the T between vowels often sounds like a soft D (flap).',
      },
      { kind: 'examples', items: ['water → wader', 'better → bedder'] },
      {
        kind: 'p',
        text: 'In British English, the T sound is usually clearer.',
      },
      { kind: 'h3', text: 'Other Englishes Around the World' },
      {
        kind: 'p',
        text: 'Because English is spoken globally, people speak it with different accents, rhythms, and expressions depending on their linguistic background.',
      },
      { kind: 'h3', text: '1. Indian English' },
      {
        kind: 'p',
        text: 'India has one of the largest English speaking populations in the world. Indian English is influenced by many local languages. Examples:',
      },
      {
        kind: 'examples',
        items: ['“**Kindly** send the document today.”', '“Please **revert** to me soon.”'],
      },
      {
        kind: 'p',
        text: 'These expressions may sound unusual to American speakers, but they are very common and correct in Indian English.',
      },
      { kind: 'h3', text: '2. Nigerian English' },
      {
        kind: 'p',
        text: 'Nigeria is one of the largest English speaking countries in Africa. Nigerian English includes expressions influenced by local languages and culture. Examples:',
      },
      {
        kind: 'examples',
        items: [
          '“I will **dash** you this book.” (I will give you this book.)',
          '“You are **not serious**!” (You are joking!)',
        ],
      },
      { kind: 'h3', text: '3. Chinese English' },
      {
        kind: 'p',
        text: 'Many Chinese English speakers may have pronunciation patterns influenced by Mandarin or other Chinese languages. For example:',
      },
      {
        kind: 'list',
        items: [
          'Difficulty pronouncing r and l sound',
          'Adding vowels at the end of consonant clusters (“school” → “suh-cool”)',
        ],
      },
      {
        kind: 'p',
        text: 'Even with these differences, communication is usually clear and intelligible.',
      },
      { kind: 'h3', text: '4. French English' },
      {
        kind: 'p',
        text: 'French speakers often bring French pronunciation and rhythm into their English. Some common features include:',
      },
      {
        kind: 'list',
        items: [
          'Pronouncing the “H”: In French, the letter H is usually silent, so some learners may not pronounce it in English. (e.g. house → \'ouse; hotel → \'otel)',
          'Difficulty with the “TH” sound: The TH sound does not exist in French, so it is sometimes replaced with Z or S sound. (e.g. this → zis; think → sink)',
          'Word stress: French words usually have stress at the end, while English stress can appear in different syllables. This may give French English a different rhythm.',
        ],
      },
      { kind: 'h3', text: '5. Spanish English' },
      {
        kind: 'p',
        text: 'Spanish speakers also bring pronunciation patterns from Spanish into English.',
      },
      {
        kind: 'list',
        items: [
          'Adding vowels before words that start with S: In Spanish, words rarely begin with S + consonant, so speakers may add an extra vowel sound (e.g. school → eschool; student → estudent)',
          'Confusion between B and V sounds: In Spanish, B and V are pronounced very similarly, which may cause confusion in English. (e.g. very → bery, vote → bote.)',
        ],
      },
      { kind: 'h3', text: '6. Brazilian English' },
      {
        kind: 'p',
        text: 'Just like other global varieties, Brazilian speakers also bring influences from Portuguese when speaking English. Some common examples include:',
      },
      {
        kind: 'examples',
        items: [
          '“I **have 30 years**.” → “I **am 30 years old**.”',
          '“I **did a travel** last year.” → “I **traveled** last year” or “I **did a trip** last year”.',
          '“I **went to the shopping**.” → “I **went shopping**” or “I **went to the mall**.”',
        ],
      },
      {
        kind: 'p',
        text: 'These expressions happen because Portuguese grammar and vocabulary influence English.',
      },
      {
        kind: 'p',
        text: 'Another common difficulty is pronunciation, such as:',
      },
      {
        kind: 'list',
        items: [
          'adding vowels to the end of words (“work” → “worki”)',
          'pronouncing TH as T or D (“think” → “tink”, “this” → “dis”)',
          'not differentiating between long and short vowels (e.g. ship vs. sheep, bit vs. beat)',
        ],
      },
      {
        kind: 'p',
        text: 'These features are normal in multilingual speech and happen when languages interact.',
      },
      { kind: 'h3', text: 'Intelligibility is the Goal' },
      {
        kind: 'p',
        text: 'Because English is spoken by people from many linguistic backgrounds, having a perfect “native speaker accent” is not necessary. What matters most is intelligibility, which means that people can understand each other.',
      },
      {
        kind: 'p',
        text: 'Many linguists believe that English belongs to everyone who speaks it, not only to people from countries like the United States or the United Kingdom. Your accent shows your history, your culture, the languages you speak.',
      },
      {
        kind: 'p',
        text: 'Instead of trying to eliminate your accent, the goal of learning English should be clear communication and confidence.',
      },
      { kind: 'p', text: 'Sources used to write this material:' },
      {
        kind: 'list',
        items: [
          'https://www.uoc.edu/portal/en/servei-linguistic/convencions/british-american-english/spelling/index.html',
          'https://www.grin.com/document/202862',
          'https://www.thoughtco.com/american-english-to-british-english-4010264',
        ],
      },
    ],
  },
  {
    slug: 'sentence-connectors',
    title: 'Sentence Connectors: Overview',
    blocks: [
      {
        kind: 'p',
        text: 'Creating longer texts such as job application letters, college application essays, and reports requires you to string together many small and separate ideas into a larger, unified whole. However, if you do not have discourse connectors, words and phrases and sentences to tie those separate ideas together, all you will have is fragments.',
      },
      {
        kind: 'p',
        text: 'By using discourse connectors, you show your readers how everything relates to everything else: something that is absolutely essential if you are going to get your readers to completely understand your ideas.',
      },
      { kind: 'h3', text: '1. Take a look at the following example' },
      {
        kind: 'examples',
        items: [
          'Maria worked in sales for five years, and she worked another three in accounting. She is eligible for a management position.',
        ],
      },
      { kind: 'h3', text: '2. Now, read over the reviewed version' },
      {
        kind: 'examples',
        items: [
          'Maria worked in sales for five years, and she worked another three in accounting. **Therefore/As a consequence**, she is eligible for a management position.',
        ],
      },
      {
        kind: 'table',
        header: ['Type of relationship', 'Examples', 'Position in clause'],
        rows: [
          [
            'Adding something',
            'Moreover; In addition; Additionally; Further; Further to this; Also; Besides that; What is more.',
            'Initial position',
          ],
          [
            'Making a contrast between two separate things, people, ideas, etc.',
            'However; On the one hand, on the other hand; In contrast; Yet.',
            'Initial position',
          ],
          [
            'Making an unexpected contrast (concession)',
            'Although; Even though; Despite the fact that; In spite of the fact that; Regardless of the fact that.',
            'Initial position. Starts a second/subordinate clause',
          ],
          [
            'Saying why something is the case',
            'Because; Since; As; Insofar as;',
            'Initial position. Starts a second/subordinate clause',
          ],
          [
            'Saying what the result of something is',
            'Therefore; Consequently; In consequence; As a result; Accordingly; Hence; Thus; For this reason; Because of this.',
            'Initial position',
          ],
          [
            'Expressing a condition',
            'If; In the event of; As long as...; So long as...; Provided that...; Assuming that...; Given that....',
            'Initial position. Starts a second/subordinate clause',
          ],
          [
            'Making what you say stronger',
            'On the contrary; As a matter of fact; In fact; Indeed.',
            'Initial position',
          ],
        ],
      },
      {
        kind: 'note',
        text: 'A number of connectors can also be used in other positions in the sentence.',
      },
      {
        kind: 'examples',
        items: [
          'Miguel worked in sales for five years, and he worked another three in accounting. He is **therefore** eligible for a management position.',
        ],
      },
      {
        kind: 'p',
        text: 'Regardless of the arrangement, using connectors makes the associations between sentences much clearer. Consequently, they make your sentences less “choppy.”',
      },
    ],
  },
  {
    slug: 'connectors-of-contrast',
    title: 'Connectors of Contrast',
    blocks: [
      {
        kind: 'p',
        text: 'We use linking words to join ideas together when we\'re talking or writing.',
      },
      {
        kind: 'list',
        items: [
          'Sometimes we want to link two ideas that are very different from each other (for example, one is a positive idea and one is a negative idea): this is a contrast.',
          'Sometimes we want to link one idea to another one that is surprising or unexpected: this is a concession.',
        ],
      },
      { kind: 'h3', text: 'Connecting two ideas in different sentences' },
      { kind: 'h3', text: '1. However = Still (no entanto/entretanto)' },
      {
        kind: 'p',
        text: 'We add these connectors to the second sentence, and we can put it at the beginning, at the end, or after the subject. They are always followed by a comma.',
      },
      {
        kind: 'examples',
        items: [
          'Many people think English is an easy language to learn. **However**, this is not necessarily true.',
          'English is a simple language. **Still**, we must learn many things to master it completely.',
        ],
      },
      { kind: 'h3', text: 'Connecting two ideas in the same sentence' },
      { kind: 'h3', text: '2. Although = Even though (embora)' },
      {
        kind: 'p',
        text: 'Used before a complete sentence (subject + verb) to show contrast.',
      },
      {
        kind: 'examples',
        items: [
          '**Even though** it is true that English is easier than many foreign languages, it can be very confusing.',
          '**Although** I like chocolate, I cannot eat it every day.',
        ],
      },
      { kind: 'h3', text: '3. Despite = In spite of (apesar de)' },
      {
        kind: 'p',
        text: 'We use \'despite\' or \'in spite of\' before a noun/noun phrase or a gerund (-ing).',
      },
      {
        kind: 'examples',
        items: [
          'I love London **despite** the bad weather',
          '**In spite of** presenting bad weather, I love London.',
        ],
      },
      { kind: 'h3', text: '4. “Despite the fact that” = “In spite of the fact that”' },
      {
        kind: 'p',
        text: 'You use these before a clause (subject + verb + complement).',
      },
      {
        kind: 'examples',
        items: [
          '**Despite the fact that** Richard got a B+, he needs to have a tutor.',
          '**In spite of the fact that** the weather is bad, I love London.',
          'I love London **despite the fact that** the weather is bad.',
        ],
      },
      { kind: 'h3', text: '5. While = Whereas = Whilst (enquanto)' },
      {
        kind: 'p',
        text: 'We use them to link two clauses (subject + verb + complement) that express a contrast between two facts or two ideas.',
      },
      {
        kind: 'examples',
        items: [
          '**While** I love my job, I also love resting.',
          'I like going out and socializing **whereas** my partner prefers to stay home resting.',
        ],
      },
      { kind: 'note', text: 'Whilst is considered more formal and literary.' },
    ],
  },
  {
    slug: 'connectors-cause-effect',
    title: 'Connectors of Cause and Effect',
    blocks: [
      {
        kind: 'p',
        text: 'Connectors of cause and effect show the relationship between two ideas, linking a cause to its result. Common connectors include because, since, as (for cause) and so, therefore, consequently, as a result (for effect). Phrases like due to and because of can also be used to introduce a cause.',
      },
      { kind: 'h3', text: 'For introducing the cause' },
      {
        kind: 'list',
        items: [
          'Because: I stayed home **because** it was raining.',
          'Since: **Since** the road was being repaired, we had to take another route.',
          'As: **As** the price of oil increased, bus fares went up.',
          'Due to (the fact): The airport closed **due to** drought conditions.',
          'Because of: The bridge was closed **because of** instability.',
        ],
      },
      { kind: 'h3', text: 'For introducing the effect/consequence' },
      {
        kind: 'list',
        items: [
          'So: It was raining, **so** I stayed home.',
          'Therefore: The sun was in the driver\'s face; **therefore**, he couldn\'t see the turn-off.',
          'Consequently: The bridge was unstable; **consequently**, engineers closed it.',
          'As a result: The price of oil increased; **as a result**, bus fares went up.',
        ],
      },
      {
        kind: 'note',
        text: 'Connectors of effect appear after some kind of punctuation (comma, semicolon or period).',
      },
    ],
  },
  {
    slug: 'conjunctions',
    title: 'Conjunctions',
    blocks: [
      {
        kind: 'p',
        text: 'Conjunctions are words that link other words, phrases, or clauses together. They allow you to form complex, elegant sentences and avoid the choppiness of multiple short sentences.',
      },
      {
        kind: 'examples',
        items: [
          'I like cooking **and** eating, **but** I don’t like washing dishes afterwards.',
          'Sophie is clearly exhausted, **yet** she insists on dancing till dawn.',
        ],
      },
      { kind: 'h3', text: '1. Coordinating conjunctions' },
      {
        kind: 'p',
        text: 'Coordinating conjunctions allow you to join words, phrases, and clauses of equal grammatical rank in a sentence. The most common coordinating conjunctions are for, and, nor, but, or, yet, and so; you can remember them by using the mnemonic device FANBOYS.',
      },
      {
        kind: 'examples',
        items: [
          'I’d like pizza for lunch. I’d like a salad for lunch. → I’d like pizza **or** a salad for lunch.',
          'Today Jane Austen is one of the most widely read English novelists. She achieved little fame during her lifetime. → Today Jane Austen is one of the most widely read English novelists, **but** she achieved little fame during her lifetime.',
          'We needed a place to concentrate. We went to the library. → We needed a place to concentrate, **so** we went to the library.',
        ],
      },
      {
        kind: 'table',
        header: ['Independent Clause', 'Coordinating Conjunction', 'Independent Clause', 'Revised Sentence'],
        rows: [
          [
            'I will not be attending the dance.',
            'for (indicates a reason or cause)',
            'I have no one to go with.',
            'I will not be attending the dance, **for** I have no one to go with.',
          ],
          [
            'I plan to stay home.',
            'and (joins two ideas)',
            'I will complete an essay for class.',
            'I plan to stay home, **and** I will complete an essay for class.',
          ],
          [
            'Jessie isn’t going to be at the dance.',
            'nor (indicates a negative)',
            'Tom won’t be there either.',
            'Jessie isn’t going to be at the dance, **nor** will Tom be there.',
          ],
          [
            'The fundraisers are hoping for a record-breaking attendance.',
            'but (indicates a contrast)',
            'I don’t think many people are going.',
            'The fundraisers are hoping for a record-breaking attendance, **but** I don’t think many people are going.',
          ],
          [
            'I might go to the next fundraising event.',
            'or (offers an alternative)',
            'I might donate some money to the cause.',
            'I might go to the next fundraising event, **or** I might donate some money to the cause.',
          ],
          [
            'My parents are worried that I am antisocial.',
            'yet (indicates a reason)',
            'I have many friends at school.',
            'My parents are worried that I am antisocial, **yet** I have many friends at school.',
          ],
          [
            'Buying a new dress is expensive.',
            'so (indicates a result)',
            'By staying home I will save money.',
            'Buying a new dress is expensive, **so** by staying home I will save money.',
          ],
        ],
      },
      { kind: 'h3', text: '2. Correlative conjunctions' },
      {
        kind: 'p',
        text: 'Correlative conjunctions are pairs of conjunctions that work together. Some examples are either/or, neither/nor, and not only/but also.',
      },
      {
        kind: 'examples',
        items: [
          '**Not only** am I finished studying English, **but** I’m **also** finished writing my essay.',
          'I have finished **neither** my English essay **nor** my history essay.',
        ],
      },
      { kind: 'h3', text: '3. Subordinating conjunctions' },
      {
        kind: 'p',
        text: 'Subordinating conjunctions join independent and dependent clauses. A subordinating conjunction can signal a cause-and-effect relationship, a contrast, or some other kind of relationship between the clauses. Common subordinating conjunctions are because, since, as, although, though, while, and whereas.',
      },
      {
        kind: 'examples',
        items: [
          'I drank a glass of water **because** I was thirsty.',
          '**Even though** Tracy would be late for work, she stopped to help the injured man.',
          'John’s friends snuck into his apartment to set up a surprise party **while** he was at work.',
        ],
      },
      {
        kind: 'note',
        text: 'Make sure that the phrases joined by conjunctions are parallel.',
      },
      {
        kind: 'examples',
        items: [
          'INCORRECT: I work **quickly and careful**.',
          'CORRECT: I work **quickly and carefully**.',
          'INCORRECT: I don’t like **running or to swim**.',
          'CORRECT: I don’t like **running or swimming**.',
          'INCORRECT: She planned to collect data by **either using an online survey or phone interviews**.',
          'CORRECT: She planned to collect data by **either using an online survey or conducting phone interviews**.',
        ],
      },
    ],
  },
];
